import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Clock, AlertTriangle } from 'lucide-react';

// Configuration
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_LOGOUT = 3 * 60 * 1000; // 3 minutes before logout
const WARNING_TIME = INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT; // 27 minutes

export default function SessionTimeout() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(180); // 3 minutes in seconds
  
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset all timers
  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Hide warning if shown
    setShowWarning(false);
    setCountdown(180);
    
    // Set warning timer (27 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(180);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, WARNING_TIME);
    
    // Set logout timer (30 minutes)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);
  }, []);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Only reset if user is logged in and warning is not shown
    if (user && !showWarning) {
      resetTimers();
    }
  }, [user, showWarning, resetTimers]);

  // Handle logout
  const handleLogout = useCallback(() => {
    // Clear all timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowWarning(false);
    logout();
  }, [logout]);

  // Continue session
  const handleContinue = useCallback(() => {
    setShowWarning(false);
    resetTimers();
  }, [resetTimers]);

  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Throttle activity detection to avoid excessive resets
    let throttleTimer = null;
    const throttledActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, 1000); // Only process activity every 1 second
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledActivity, { passive: true });
    });

    // Initialize timers
    resetTimers();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [user, handleActivity, resetTimers]);

  // Auto-logout when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && showWarning) {
      handleLogout();
    }
  }, [countdown, showWarning, handleLogout]);

  // Don't render anything if user is not logged in
  if (!user) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="h-5 w-5" />
            Sesión a punto de expirar
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>
              Tu sesión se cerrará automáticamente por inactividad.
            </p>
            <div className="flex items-center justify-center gap-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Clock className="h-6 w-6 text-orange-500" />
              <span className="text-2xl font-bold text-orange-600">
                {formatTime(countdown)}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Haz clic en "Continuar" para mantener tu sesión activa.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogAction
            onClick={handleLogout}
            className="bg-slate-200 text-slate-700 hover:bg-slate-300"
          >
            Cerrar sesión
          </AlertDialogAction>
          <AlertDialogAction
            onClick={handleContinue}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="continue-session-btn"
          >
            Continuar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
